import {
  useCallback, useMemo, useState,
} from 'react';

import {
  DEFAULT_AUDIENCE_TYPE,
  DEFAULT_COUNTRIES,
  DEFAULT_SALES_STAGES,
  getDefaultDatesForAudienceType,
  isClientAudience,
} from '../utils/constants';
import {
  isBodySendable,
  substitutedLength,
} from '../utils/composeBodyPreview';
import { generateCampaignName } from '../utils/generateCampaignName';
import { toggleItemInList } from '../utils/listSelection';

const initialAudience = () => {
  const { startDate, endDate } = getDefaultDatesForAudienceType(DEFAULT_AUDIENCE_TYPE);
  return {
    audienceType: DEFAULT_AUDIENCE_TYPE,
    startDate,
    endDate,
    countries: DEFAULT_COUNTRIES,
    salesStages: DEFAULT_SALES_STAGES,
    requireConsent: true,
  };
};

export const useMassTextCompose = () => {
  const [name, setName] = useState('');
  const [coach, setCoach] = useState(null);
  const [audience, setAudience] = useState(initialAudience);
  const [dpl, setDpl] = useState(null);
  const [body, setBody] = useState('');
  const [image, setImage] = useState(null);
  const [coachResetKey, setCoachResetKey] = useState(0);

  const handleSetCoach = useCallback((next) => {
    setCoach(next);
    setDpl(null);
  }, []);

  const patchAudience = useCallback((patch) => {
    setAudience((prev) => ({ ...prev, ...patch }));
  }, []);

  const toggleCountry = useCallback((code) => {
    setAudience((prev) => ({ ...prev, countries: toggleItemInList(prev.countries, code) }));
  }, []);

  const toggleSalesStage = useCallback((stage) => {
    setAudience((prev) => ({ ...prev, salesStages: toggleItemInList(prev.salesStages, stage) }));
  }, []);

  const setAudienceType = useCallback((audienceType) => {
    setAudience((prev) => {
      if (audienceType === prev.audienceType) return prev;
      const isClients = isClientAudience(audienceType);
      return {
        ...prev,
        ...getDefaultDatesForAudienceType(audienceType),
        audienceType,
        salesStages: isClients ? [] : DEFAULT_SALES_STAGES,
        requireConsent: !isClients,
      };
    });
  }, []);

  const setRequireConsent = useCallback((value) => {
    setAudience((prev) => ({ ...prev, requireConsent: value }));
  }, []);

  const reset = useCallback(() => {
    setName('');
    setCoach(null);
    setAudience(initialAudience());
    setDpl(null);
    setBody('');
    setImage(null);
    setCoachResetKey((prev) => prev + 1);
  }, []);

  const derived = useMemo(() => {
    const coachName = coach ? coach.name : '';
    const autoGeneratedName = generateCampaignName(coachName);
    const subLen = substitutedLength(body, { coachName });
    const datesValid = !!audience.startDate
      && !!audience.endDate
      && !audience.startDate.isAfter(audience.endDate);
    const isClients = isClientAudience(audience.audienceType);
    const audienceComplete = !!(coach && coach.id)
      && audience.countries.length > 0
      && (isClients || audience.salesStages.length > 0)
      && datesValid;
    const messageComplete = isBodySendable(body, subLen);
    return {
      autoGeneratedName,
      effectiveName: name.trim() || autoGeneratedName,
      coachName,
      isClientAudience: isClients,
      audienceComplete,
      messageComplete,
      dplComplete: !!(dpl && dpl.contractId),
    };
  }, [name, coach, audience, dpl, body]);

  return {
    name,
    setName,
    coach,
    setCoach: handleSetCoach,
    audience,
    setAudience: patchAudience,
    toggleCountry,
    toggleSalesStage,
    setAudienceType,
    setRequireConsent,
    dpl,
    setDpl,
    body,
    setBody,
    image,
    setImage,
    coachResetKey,
    derived,
    reset,
  };
};
